import { useState, useEffect } from "react";
import api from "../../lib/api";
import { Loader2, TrendingUp, DollarSign, Users, Zap, ArrowUp, ArrowDown, Flame, Trophy, XCircle, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend, FunnelChart, Funnel, LabelList } from "recharts";
import { motion } from "framer-motion";

const STAGE_COLORS = { new: "#3B82F6", contacted: "#F59E0B", booked: "#6366F1", qualified: "#10B981", proposal: "#8B5CF6", closed_won: "#059669", closed_lost: "#EF4444" };
const STAGE_LABELS = { new: "New", contacted: "Contacted", booked: "Booked", qualified: "Qualified", proposal: "Proposal", closed_won: "Won", closed_lost: "Lost" };
const SOURCE_COLORS = { audit: "#0033CC", discovery: "#0033CC", inbound: "#10B981", manual: "#F59E0B", chatbot: "#7C3AED", white_glove: "#0033CC", referral: "#EC4899", website: "#0EA5E9" };
const SOURCE_LABELS = { audit: "Discovery", discovery: "Discovery", inbound: "Inbound", manual: "Manual", chatbot: "Chatbot", white_glove: "Blueprint", referral: "Referral", website: "Website" };
const TIME_RANGES = [
  { value: "30", label: "30 Days" },
  { value: "90", label: "3 Months" },
  { value: "180", label: "6 Months" },
  { value: "365", label: "12 Months" },
];

export default function OpsAnalytics({ onOpenLead }) {
  const [data, setData] = useState(null);
  const [winLoss, setWinLoss] = useState(null);
  const [crossBench, setCrossBench] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [d, wl, cb] = await Promise.all([
          api.get(`/ops/analytics/advanced?days=${range}`),
          api.get(`/ops/analytics/win-loss?days=${range}`),
          api.get("/benchmark/cross-industry/compare").catch(() => ({ data: null })),
        ]);
        setData(d.data);
        setWinLoss(wl.data);
        setCrossBench(cb.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [range]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#0033CC]" /></div>;
  if (!data && !loading) return <div className="text-center py-20 text-slate-400">No analytics data</div>;

  return (
    <div className="space-y-6" data-testid="ops-analytics">
      {/* Time Range Selector */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-['Outfit'] font-medium text-[#0F172A]">Analytics</h1>
        <div className="bg-white border border-slate-200 p-0.5 flex">
          {TIME_RANGES.map(t => (
            <button key={t.value} onClick={() => setRange(t.value)}
              className={`px-4 py-1.5 text-xs font-['Outfit'] transition-colors ${range === t.value ? "bg-[#0033CC] text-white" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`range-${t.value}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#0033CC]" /></div> : (() => {
      const pipelineChartData = Object.entries(data.pipeline_value || {})
        .filter(([k]) => k !== "closed_lost")
        .map(([k, v]) => ({ name: STAGE_LABELS[k] || k, value: v, count: data.pipeline_count?.[k] || 0 }));
      const funnelData = (data.funnel || []).map(f => ({ ...f, name: STAGE_LABELS[f.stage] || f.stage, fill: STAGE_COLORS[f.stage] || "#94A3B8" }));
      const scoreDistData = Object.entries(data.deal_score_distribution || {}).map(([k, v]) => ({ range: k, count: v }));
      const sourceData = Object.entries(data.source_breakdown || {}).map(([k, v]) => ({ name: SOURCE_LABELS[k.toLowerCase()] || k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()), key: k.toLowerCase(), value: v })).filter(s => s.value > 0);
      const monthlyData = data.monthly_trends || [];
      return <>

      {/* KPI Row */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KPI label="Total Pipeline" value={`$${(data.total_pipeline_value || 0).toLocaleString()}`} icon={DollarSign} />
        <KPI label="Weighted Forecast" value={`$${(data.weighted_forecast || 0).toLocaleString()}`} icon={TrendingUp} accent />
        <KPI label="Avg Deal Size" value={`$${(data.avg_deal_size || 0).toLocaleString()}`} icon={DollarSign} />
        <KPI label="Avg Cycle" value={`${data.avg_cycle_days || 0} days`} icon={Zap} />
        <KPI label="Total Leads" value={data.total_leads || 0} icon={Users} />
      </motion.div>

      {/* Row 2: Pipeline Value + Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white border border-slate-200 p-6" data-testid="pipeline-value-chart">
          <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Pipeline Value by Stage</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={pipelineChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B", fontFamily: "IBM Plex Sans" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 0, fontSize: 12, fontFamily: "IBM Plex Sans" }} formatter={v => [`$${v.toLocaleString()}`, "Value"]} />
              <Bar dataKey="value" fill="#0033CC" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white border border-slate-200 p-6" data-testid="conversion-funnel">
          <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Conversion Funnel</h3>
          <div className="space-y-2">
            {funnelData.map((f, i) => (
              <div key={f.stage} className="flex items-center gap-3">
                <span className="text-xs font-['IBM_Plex_Sans'] text-slate-500 w-20">{f.name}</span>
                <div className="flex-1 h-7 bg-slate-50 relative overflow-hidden">
                  <div className="h-full transition-all duration-700" style={{ width: `${f.pct}%`, backgroundColor: f.fill, opacity: 0.8 }} />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-['JetBrains_Mono'] text-slate-700">{f.count} ({f.pct}%)</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Row 3: Deal Score Distribution + Top Deals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white border border-slate-200 p-6" data-testid="deal-score-dist">
          <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-1">Deal Score Distribution</h3>
          <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-4">Higher score = hotter deal</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={scoreDistData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="range" tick={{ fontSize: 10, fill: "#64748B", fontFamily: "JetBrains Mono" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
              <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="count" fill="#0033CC">
                {scoreDistData.map((_, i) => (
                  <Cell key={`idx-${i}`} fill={["#CBD5E1", "#94A3B8", "#6366F1", "#0033CC", "#DC2626"][i] || "#0033CC"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white border border-slate-200 p-6" data-testid="top-deals">
          <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-1 flex items-center gap-2"><Flame className="w-4 h-4 text-red-500" />Hottest Deals</h3>
          <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-4">Ranked by Deal Score — work these next</p>
          <div className="space-y-2">
            {(data.top_deals || []).slice(0, 7).map((d, i) => (
              <div key={d.id} onClick={() => onOpenLead?.(d.id)} className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-200 transition-all" data-testid={`top-deal-${i}`}>
                <span className={`w-6 h-6 flex items-center justify-center text-xs font-['JetBrains_Mono'] font-bold ${i < 3 ? "bg-[#0033CC] text-white" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-['IBM_Plex_Sans'] font-medium text-[#0F172A] truncate">{d.name}</div>
                  <div className="text-xs text-slate-400 truncate">{d.company}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-['JetBrains_Mono'] font-bold" style={{ color: d.deal_score >= 60 ? "#DC2626" : d.deal_score >= 40 ? "#F59E0B" : "#64748B" }}>{d.deal_score}</div>
                  {d.deal_value > 0 && <div className="text-xs font-['JetBrains_Mono'] text-slate-400">${d.deal_value.toLocaleString()}</div>}
                </div>
              </div>
            ))}
            {(!data.top_deals || data.top_deals.length === 0) && <p className="text-sm text-slate-400 text-center py-4">No active deals</p>}
          </div>
        </motion.div>
      </div>

      {/* Row 4: Monthly Trends + Source Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="lg:col-span-2 bg-white border border-slate-200 p-6" data-testid="monthly-trends">
          <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Monthly Trends</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748B", fontFamily: "JetBrains Mono" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
              <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 0, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Sans" }} />
              <Area type="monotone" dataKey="created" stackId="1" stroke="#0033CC" fill="#0033CC" fillOpacity={0.15} name="Created" />
              <Area type="monotone" dataKey="won" stackId="2" stroke="#059669" fill="#059669" fillOpacity={0.2} name="Won" />
              <Area type="monotone" dataKey="lost" stackId="3" stroke="#EF4444" fill="#EF4444" fillOpacity={0.1} name="Lost" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white border border-slate-200 p-6" data-testid="source-breakdown">
          <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Lead Sources</h3>
          {sourceData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={sourceData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" labelLine={false}>
                    {sourceData.map((entry) => (
                      <Cell key={entry.name} fill={SOURCE_COLORS[entry.key] || "#94A3B8"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 0, fontSize: 12, fontFamily: "'IBM Plex Sans', sans-serif" }} formatter={(value, name) => [value, name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-5 mt-3">
                {sourceData.map(s => (
                  <div key={s.name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 flex-shrink-0" style={{ backgroundColor: SOURCE_COLORS[s.key] || "#94A3B8" }} />
                    <span className="text-xs font-['IBM_Plex_Sans'] text-slate-600">{s.name}</span>
                    <span className="text-xs font-['JetBrains_Mono'] text-slate-400">{s.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-400 text-center py-8 font-['IBM_Plex_Sans']">No lead source data yet</p>
          )}
        </motion.div>
      </div>

      {/* Row 5: Win/Loss Analysis */}
      {winLoss && winLoss.total_closed > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="space-y-6" data-testid="win-loss-analysis">
          <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] flex items-center gap-2">Win/Loss Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 p-4">
              <p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Win Rate</p>
              <div className="text-2xl font-['Outfit'] font-light text-emerald-600">{winLoss.win_rate}%</div>
              <p className="text-xs text-slate-400 mt-1">{winLoss.won_count}W / {winLoss.lost_count}L</p>
            </div>
            <div className="bg-white border border-slate-200 p-4">
              <p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Won Revenue</p>
              <div className="text-2xl font-['Outfit'] font-light text-emerald-600">${(winLoss.total_won_value || 0).toLocaleString()}</div>
            </div>
            <div className="bg-white border border-slate-200 p-4">
              <p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Lost Revenue</p>
              <div className="text-2xl font-['Outfit'] font-light text-red-500">${(winLoss.total_lost_value || 0).toLocaleString()}</div>
            </div>
            <div className="bg-white border border-slate-200 p-4">
              <p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Total Closed</p>
              <div className="text-2xl font-['Outfit'] font-light text-[#0F172A]">{winLoss.total_closed}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Win reasons */}
            <div className="bg-white border border-emerald-200 p-5">
              <h4 className="text-sm font-['Outfit'] font-medium text-emerald-800 mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-emerald-600" />Win Reasons</h4>
              {Object.entries(winLoss.win_categories || {}).length > 0 ? (
                <div className="space-y-2 mb-4">
                  {Object.entries(winLoss.win_categories).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between">
                      <span className="text-xs font-['IBM_Plex_Sans'] text-slate-600 capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="text-xs font-['JetBrains_Mono'] text-emerald-700 bg-emerald-50 px-2 py-0.5">{v}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-slate-400 mb-3">No wins recorded yet</p>}
              {(winLoss.recent_wins || []).slice(0, 3).map((w, i) => (
                <div key={`idx-${i}`} className="border-t border-emerald-100 pt-2 mt-2">
                  <div className="text-xs font-['IBM_Plex_Sans'] text-slate-700 font-medium">{w.company} {w.value > 0 && `($${w.value.toLocaleString()})`}</div>
                  <div className="text-xs text-slate-500">{w.reason}</div>
                </div>
              ))}
            </div>
            {/* Loss reasons */}
            <div className="bg-white border border-red-200 p-5">
              <h4 className="text-sm font-['Outfit'] font-medium text-red-800 mb-3 flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" />Loss Reasons</h4>
              {Object.entries(winLoss.loss_categories || {}).length > 0 ? (
                <div className="space-y-2 mb-4">
                  {Object.entries(winLoss.loss_categories).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between">
                      <span className="text-xs font-['IBM_Plex_Sans'] text-slate-600 capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="text-xs font-['JetBrains_Mono'] text-red-700 bg-red-50 px-2 py-0.5">{v}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-slate-400 mb-3">No losses recorded yet</p>}
              {(winLoss.recent_losses || []).slice(0, 3).map((l, i) => (
                <div key={`idx-${i}`} className="border-t border-red-100 pt-2 mt-2">
                  <div className="text-xs font-['IBM_Plex_Sans'] text-slate-700 font-medium">{l.company} {l.value > 0 && `($${l.value.toLocaleString()})`}</div>
                  <div className="text-xs text-slate-500">{l.reason}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
      </>; })()}

      {/* Cross-Industry Benchmark Comparison */}
      {crossBench?.industries && crossBench.industries.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <div className="bg-white border border-slate-200 p-6" data-testid="cross-industry-benchmarks">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-[#0033CC]" strokeWidth={1.5} />
              <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Cross-Industry Benchmarks</h3>
            </div>
            <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-5">Average AI readiness scores across industries</p>
            <div className="space-y-2.5">
              {crossBench.industries.map((ind, i) => {
                const maxScore = crossBench.industries[0]?.overall || 100;
                return (
                  <div key={ind.industry} className="flex items-center gap-3" data-testid={`bench-industry-${ind.industry}`}>
                    <span className="text-xs font-['JetBrains_Mono'] text-slate-400 w-5 text-right">{i + 1}</span>
                    <span className="text-xs font-['Outfit'] font-medium text-[#0F172A] w-36 truncate">{ind.label}</span>
                    <div className="flex-1 h-5 bg-slate-50 relative overflow-hidden">
                      <div
                        className="h-full transition-all duration-500"
                        style={{ width: `${(ind.overall / maxScore) * 100}%`, background: ind.audit_count > 0 ? "#0033CC" : "#CBD5E1" }}
                      />
                      <span className="absolute right-2 top-0.5 text-[10px] font-['JetBrains_Mono'] text-slate-500">{ind.overall}/100</span>
                    </div>
                    <span className="text-[10px] font-['JetBrains_Mono'] text-slate-400 w-16 text-right">
                      {ind.audit_count > 0 ? `${ind.audit_count} blueprint${ind.audit_count !== 1 ? "s" : ""}` : "baseline"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function KPI({ label, value, icon: Icon, accent }) {
  return (
    <div className={`border p-4 ${accent ? "bg-[#0033CC]/[0.03] border-[#0033CC]/20" : "bg-white border-slate-200"}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent ? "text-[#0033CC]" : "text-slate-400"}`} strokeWidth={1.5} />
        <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-slate-400">{label}</p>
      </div>
      <div className={`text-2xl font-['Outfit'] font-light ${accent ? "text-[#0033CC]" : "text-[#0F172A]"}`}>{value}</div>
    </div>
  );
}
